// import ImageHTML from '../imageHTML/imageHTML';
import ImageHTML from '../imageHTML/imgHTML';
import { dateFormatDDmesAnio } from '../../functions/functions';
import './cardResults.sass'

/**
 * Componente de renderizado para lista de resultados de búsqueda con tracking avanzado
 * 
 * @description Renderiza una lista de tarjetas de resultados con funcionalidad completa
 * incluyendo imágenes, metadatos, indicadores multimedia y tracking de interacciones.
 * Implementa comunicación iframe para analytics y navegación contextual apropiada.
 * Optimizado para contenido rico con soporte para videos, categorías y fechas.
 * 
 * **Características principales:**
 * - Renderizado de lista con validación defensiva
 * - Tracking de clicks con postMessage para analytics
 * - Indicadores visuales para contenido multimedia
 * - Navegación iframe con target apropiado
 * - Metadatos ricos (fecha, categoría, título)
 * - Keys optimizadas para rendimiento de React
 * - Accesibilidad con ARIA labels
 * 
 * **Validación defensiva:**
 * - Maneja arrays vacíos o datos inválidos
 * - Fallbacks seguros para propiedades faltantes
 * - Estrategia de keys robusta con múltiples fallbacks
 * 
 * @component
 * @param {Object} props - Propiedades del componente
 * @param {Array} [props.data=[]] - Array de objetos de resultados de búsqueda
 * @param {string} props.data[].id - Identificador único del resultado (preferido para keys)
 * @param {string} props.data[].title - Título del artículo o contenido
 * @param {string} props.data[].url - URL de destino del resultado
 * @param {string} props.data[].image - URL de la imagen de preview
 * @param {string} props.data[].category - Categoría principal del contenido
 * @param {string} [props.data[].subcategory] - Subcategoría (prioridad sobre category)
 * @param {string} props.data[].date - Fecha de publicación (formato ISO)
 * @param {Array<string>} [props.data[].tags] - Tags del contenido (ej: ["video", "destacado"])
 * 
 * @returns {JSX.Element|null} Lista de tarjetas de resultados o null si no hay datos
 * 
 * @since 1.0.0
 * @author Valeria Garcia
 * 
 * @see {@link ../imageHTML/imgHTML.js} Componente para renderizado de imágenes
 * @see {@link ./results.js} Componente padre que utiliza CardResults
 * @see {@link ../../functions/functions.js#dateFormatDDmesAnio} Función de formateo de fechas
 * @see {@link ../../constantes/constantes.js#PATHURLDOMAIN} Dominio para postMessage
 */
export function CardResult({ item }) {
  const { title, url, publish_date, image, category, subcategory, type } = item;
  const iconClass = `icon-default ${subcategory ? 'icon-' + subcategory : ''} ${category ? 'icon-' + category : ''}`;
  const categoryText = (subcategory || category || '').replace(/-/g, ' ');
  const publishedDate = dateFormatDDmesAnio(publish_date);

  return (
    <div className="box-list-card">
      <a className="item-card" href={url} title={title} target="_parent" rel="bookmark">
        <div className="thumb">
          <ImageHTML url={image} title={title} type={type}/>
        </div>

        <div className="info">
          <h4 className={iconClass}>{categoryText}</h4>
          <h3>{title}</h3>
          <time dateTime={publish_date} className="datetime" aria-label={`Publicado el ${publishedDate}`}>
            {publishedDate}
          </time>
        </div>
      </a>
    </div>
  );
}

export default function CardResults({ data = [] }) {
  if (!Array.isArray(data) || data.length === 0) {
    return null;
  }

  return data.map((item, index) => (
    <CardResult
      key={item.id || item.url || `${item.title || 'resultado'}-${index}`}
      item={item}
    />
  ));
}